import { useMemo } from "react";
import { Link } from "react-router-dom";
import isAuthenticated from "../../components/Utils/isAuthenticated";
import { getNavGroupRoutes, getNavGroupExternalLinks } from "../../config/routes";
import Footer from "../RFS/components/Footer";
import LaunchIcon from "@mui/icons-material/Launch";

const quickLinks = [
	{ id: "overview", label: "Overview" },
	{ id: "schedule", label: "When SPOT Occurs" },
	{ id: "process", label: "SPOT Process" },
	{ id: "results", label: "Accessing Results" },
	{ id: "policy-and-privacy", label: "Reports and Privacy" },
	{ id: "customization", label: "Customization and Team Teaching" },
	{ id: "support", label: "Support and Troubleshooting" },
];

const processSteps = [
	"Before the feedback period starts, instructors receive an email announcement with key dates and instructions.",
	"Instructors should notify students that SPOT is opening and encourage participation.",
	"When surveys open, students receive an email with instructions and the survey link.",
	"During the response period, Test Scoring and Questionnaire Services sends instructors a reminder with response rate guidance.",
	"Students who have not completed their surveys receive reminder emails.",
	"After surveys close and grades are finalized, results are available in the SPOT reporting system.",
];

const anonymityMeasures = [
	"The survey administrator cannot identify a student through survey tools unless the student self-identifies.",
	"The survey tools are designed to be anonymous.",
	"Student IDs and usernames are not tagged on survey results.",
	"Students log in only to verify whether they have taken a survey; that verification is separate from their answers.",
	"The policy allows anonymity to be overridden only in limited safety-related cases.",
];

const troubleshootingSteps = [
	"Clear browser cache and cookies: https://universityofalberta.freshservice.com/support/solutions/articles/19000110095.",
	"Check CCID status: https://myccid.ualberta.ca/check.",
	"If issues continue, contact the Staff Service Centre: https://www.ualberta.ca/en/services/staff-service-centre/index.html.",
];

export default function ReportingPage() {
	const auth = useMemo(() => isAuthenticated(), []);
	const userAccess = useMemo(
		() =>
			auth.access
				? auth.access
						.split(",")
						.map((a) => a.trim())
						.filter(Boolean)
				: [],
		[auth.access],
	);

	const routes = useMemo(() => getNavGroupRoutes("reporting", userAccess), [userAccess]);
	const externalLinks = useMemo(
		() => getNavGroupExternalLinks("reporting", userAccess),
		[userAccess],
	);

	const hasOptions = routes.length > 0 || externalLinks.length > 0;

	return (
		<div className="bg-digitalWhite min-h-[78svh] flex flex-col">
			<div className="w-full mx-auto px-6 md:px-14">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 py-6 md:py-12">
					<div className="col-span-1 md:col-span-2 bg-white rounded-xl shadow-sm px-6 md:px-10 py-8 space-y-10">
						<section id="overview" className="scroll-mt-24">
							<h1 className="text-2xl font-semibold text-primary mb-1">
								Student Perspectives of Teaching (SPOT)
							</h1>
							<p className="text-gray-500 text-sm mb-6">
								Effective July 1, 2023, SPOT uses a survey with 18 questions across 6 sections to
								collect student feedback on teaching and learning experiences.
							</p>

							<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
								<p className="text-sm text-gray-600 leading-relaxed mb-3">
									SPOT helps instructors, departments, and faculties improve curriculum and
									instruction. Collection and use of SPOT feedback follows University of Alberta
									policy.
								</p>
								<ul className="flex flex-col gap-2 text-sm">
									<li>
										<a
											href="https://www.ualberta.ca/en/provost/policies-and-procedures/student-perspectives-of-teaching-spot.html"
											target="_blank"
											rel="noreferrer"
											className="inline-flex items-center gap-1 text-primary font-medium hover:underline"
										>
											Student Perspectives of Teaching policy page
											<LaunchIcon sx={{ fontSize: 16 }} />
										</a>
									</li>
									<li>
										<a
											href="https://www.ualberta.ca/en/alfresco/uappol/academic/teaching-learning-and-evaluation/policy/teaching-learning-and-evaluation-policy.pdf"
											target="_blank"
											rel="noreferrer"
											className="inline-flex items-center gap-1 text-primary font-medium hover:underline"
										>
											Teaching, Learning and Evaluation Policy
											<LaunchIcon sx={{ fontSize: 16 }} />
										</a>
									</li>
								</ul>
							</div>
						</section>

						<section id="schedule" className="scroll-mt-24">
							<h2 className="text-xl font-semibold text-gray-800 mb-2">When SPOT Occurs</h2>
							<p className="text-sm text-gray-600 leading-relaxed">
								SPOT surveys are normally available from two weeks before the last day of classes
								until two days after the last day of classes, as long as final exams are not
								scheduled in that two-day period.
							</p>
						</section>

						<section id="process" className="scroll-mt-24">
							<h2 className="text-xl font-semibold text-gray-800 mb-4">SPOT Process</h2>
							<ol className="flex flex-col gap-3">
								{processSteps.map((step, index) => (
									<li key={step} className="flex items-start gap-3 text-sm text-gray-600">
										<span className="inline-flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-semibold">
											{index + 1}
										</span>
										<span className="leading-relaxed">{step}</span>
									</li>
								))}
							</ol>
						</section>

						<section id="results" className="scroll-mt-24">
							<h2 className="text-xl font-semibold text-gray-800 mb-4">Accessing Results</h2>
							<ul className="flex flex-col gap-3 text-sm text-gray-600 leading-relaxed">
								<li className="rounded-lg border border-gray-200 bg-gray-50 p-4">
									Instructors receive SPOT results within 20 working days after course completion,
									once the dean, director, or chair has signed the grade sheet.
								</li>
								<li className="rounded-lg border border-gray-200 bg-gray-50 p-4">
									During the SPOT period, response rates, survey links, QR codes, survey dates, and
									enrollment details are available to instructors at{" "}
									<a
										href="https://ualberta.bluera.com"
										target="_blank"
										rel="noreferrer"
										className="inline-flex items-center gap-1 text-primary font-medium hover:underline"
									>
										ualberta.bluera.com
										<LaunchIcon sx={{ fontSize: 16 }} />
									</a>
									.
								</li>
								<li className="rounded-lg border border-gray-200 bg-gray-50 p-4">
									Instructor reports are available under{" "}
									<Link
										to="/reporting/my-spot-reports"
										className="text-primary font-medium hover:underline"
									>
										SPOT & USRI Reports {"->"} My SPOT Reports
									</Link>
									.
								</li>
								<li className="rounded-lg border border-gray-200 bg-gray-50 p-4">
									Dean and Chair reports are available under{" "}
									<Link
										to="/reporting/SPOT-DDC-Search"
										className="text-primary font-medium hover:underline"
									>
										SPOT & USRI Reports {"->"} Dean and Chair SPOT Reports
									</Link>
									.
								</li>
								<li className="rounded-lg border border-gray-200 bg-gray-50 p-4">
									For classes with fewer than 10 students, only commentary reports are available.
								</li>
							</ul>
						</section>

						<section id="policy-and-privacy" className="scroll-mt-24 space-y-5">
							<div>
								<h2 className="text-xl font-semibold text-gray-800 mb-2">Reports and Privacy</h2>
								<p className="text-sm text-gray-600 leading-relaxed">
									Public access to SPOT results follows section 15 of the Student Input to the
									Evaluation of Teaching and Learning Procedure.
								</p>
								<a
									href="https://www.ualberta.ca/en/alfresco/uappol/academic/teaching-learning-and-evaluation/procedure/student-input-to-the-evaluation-of-teaching-and-learning-procedure.pdf"
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 mt-2 text-primary font-medium hover:underline text-sm"
								>
									View the Student Input Procedure
									<LaunchIcon sx={{ fontSize: 16 }} />
								</a>
							</div>

							<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
								<h3 className="text-base font-semibold text-gray-800 mb-2">What reports include</h3>
								<p className="text-sm text-gray-600 leading-relaxed">
									A one-page report is generated for each class. It includes each survey question
									and response counts across the scale from strongly disagree to strongly agree.
								</p>
							</div>

							<div>
								<h3 className="text-base font-semibold text-gray-800 mb-3">
									Anonymity protections
								</h3>
								<ul className="flex flex-col gap-2">
									{anonymityMeasures.map((item) => (
										<li
											key={item}
											className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed"
										>
											<span className="mt-0.5 inline-flex shrink-0 items-center justify-center w-5 h-5 rounded bg-primary/10 text-primary">
												<CheckIcon />
											</span>
											<span>{item}</span>
										</li>
									))}
								</ul>
							</div>
						</section>

						<section id="customization" className="scroll-mt-24 space-y-4">
							<h2 className="text-xl font-semibold text-gray-800">
								Customization and Team Teaching
							</h2>
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
								<h3 className="text-base font-semibold text-gray-800 mb-2">
									Custom instructor questions
								</h3>
								<p className="text-sm text-gray-600 leading-relaxed">
									SPOT surveys in Blue (Explorance) can include instructor, department, faculty, and
									university-level items. To include instructor-specific questions, contact your
									faculty or department SPOT administrator, or email{" "}
									<a
										href="mailto:spot@ualberta.ca"
										className="text-primary font-medium hover:underline"
									>
										spot@ualberta.ca
									</a>
									.
								</p>
							</div>
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
								<h3 className="text-base font-semibold text-gray-800 mb-2">
									Multiple instructors in one course
								</h3>
								<p className="text-sm text-gray-600 leading-relaxed">
									SPOT supports team-taught courses by placing overall course questions first and
									repeating instructor-specific questions for each instructor. Each instructor sees
									the shared course questions plus their own individual results.
								</p>
							</div>
						</section>

						<section id="support" className="scroll-mt-24 space-y-4">
							<h2 className="text-xl font-semibold text-gray-800">Support and Troubleshooting</h2>
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
								<h3 className="text-base font-semibold text-gray-800 mb-2">
									Incorrect SPOT announcement details
								</h3>
								<p className="text-sm text-gray-600 leading-relaxed">
									If instructor or course details are incorrect, or if a course is missing or
									cancelled, contact TSQS promptly at{" "}
									<a href="tel:7804922741" className="text-primary font-medium hover:underline">
										780-492-2741
									</a>{" "}
									or{" "}
									<a
										href="mailto:test.scoring@ualberta.ca"
										className="text-primary font-medium hover:underline"
									>
										test.scoring@ualberta.ca
									</a>
									.
								</p>
							</div>
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
								<h3 className="text-base font-semibold text-gray-800 mb-2">
									Login problems in the SPOT system
								</h3>
								<ul className="flex flex-col gap-2">
									{troubleshootingSteps.map((step) => (
										<li
											key={step}
											className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed"
										>
											<span className="mt-0.5 inline-flex shrink-0 items-center justify-center w-5 h-5 rounded bg-primary/10 text-primary">
												<CheckIcon />
											</span>
											<span>{step}</span>
										</li>
									))}
								</ul>
							</div>
						</section>
					</div>

					<div className="w-full mt-8 md:mt-0">
						<div className="sticky top-6">
							<div className="bg-white rounded-xl shadow-sm px-6 md:px-10 py-8">
								<div className="mb-6">
									<h2 className="text-2xl font-semibold text-primary">Reporting</h2>
									<p className="text-gray-600 mt-1">
										Jump to a section or choose a destination below.
									</p>
								</div>

								<div className="mb-6">
									<h3 className="text-base font-semibold text-gray-500 uppercase tracking-wide mb-3">
										On This Page
									</h3>
									<ul className="grid gap-2">
										{quickLinks.map((item) => (
											<li key={item.id}>
												<a
													href={`#${item.id}`}
													className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:border-primary hover:shadow-md"
												>
													<span className="text-primary font-semibold text-sm">{item.label}</span>
												</a>
											</li>
										))}
									</ul>
								</div>

								{!hasOptions && (
									<div className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-gray-600">
										No reporting options available for your account.
									</div>
								)}

								{routes.length > 0 && (
									<div className="mb-6">
										<h3 className="text-base font-semibold text-gray-500 uppercase tracking-wide mb-3">
											Internal Pages
										</h3>
										<ul className="grid gap-3 sm:grid-cols-1">
											{routes.map((route) => (
												<li key={route.path}>
													<Link
														to={route.path}
														className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition hover:border-primary hover:shadow-md"
													>
														<span className="text-primary font-semibold">{route.label}</span>
													</Link>
												</li>
											))}
										</ul>
									</div>
								)}

								{externalLinks.length > 0 && (
									<div>
										<h3 className="text-base font-semibold text-gray-500 uppercase tracking-wide mb-3">
											External Links
										</h3>
										<ul className="grid gap-3 sm:grid-cols-1">
											{externalLinks.map((link) => (
												<li key={link.url}>
													<a
														href={link.url}
														target="_blank"
														rel="noreferrer"
														className="block rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition hover:border-primary hover:shadow-md"
													>
														<span className="text-primary font-semibold inline-flex items-center gap-1">
															{link.label}
															<LaunchIcon sx={{ fontSize: 16 }} />
														</span>
														<span className="block text-xs text-gray-500 mt-1">
															Opens in a new tab
														</span>
													</a>
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</div>
	);
}

const CheckIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		className="w-4 h-4"
		fill="none"
		viewBox="0 0 24 24"
		stroke="currentColor"
		strokeWidth={2.5}
	>
		<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
	</svg>
);
